/**
 * Pure operations on an eval graph (schema_version 2) for the editor.
 *
 * The stored shape is `{ schema_version, entry, nodes: { id: node } }` with
 * conditions on the edges. Every function here takes a graph and returns a
 * new graph; nothing mutates. Layout is computed from the graph each time it
 * is drawn; where a person dragged a step, that position is kept on the
 * version as `ui.layout` (non-semantic: moving a step is never a new version).
 *
 * Validation is the server's (graphSchema.validate, via /definitions/validate).
 * Locally we only keep the graph WELL-FORMED enough to edit: every edge points
 * at a node that exists, and every non-verdict node has a default edge.
 */
import { NODE_KINDS, kindOf } from './nodeKinds';
import { fromApi, rowProblems } from 'shared/decision/questions';
import { read as readOutcomes, route as routeOutcome } from './outcomes';

export const NODE_W = 232;
export const NODE_H = 56;
export const V_GAP = 84;
export const H_GAP = 40;

const clone = (g) => JSON.parse(JSON.stringify(g));

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

export function nodeIds(graph) {
  return Object.keys(graph?.nodes || {});
}

/** Every edge as { from, to, index, when, isDefault, label }. */
export function edgeList(graph) {
  const out = [];
  for (const [from, node] of Object.entries(graph?.nodes || {})) {
    (node.edges || []).forEach((e, index) => {
      const isDefault = e.default !== undefined;
      out.push({
        from, to: isDefault ? e.default : e.to, index, when: e.when || null, isDefault,
        label: isDefault ? ((node.edges || []).length > 1 ? 'otherwise' : '') : (e.name || describeCondition(e.when)),
      });
    });
  }
  return out;
}

export function describeCondition(when) {
  if (!when) return '';
  if (Array.isArray(when.all)) return when.all.map(describeCondition).join(' and ');
  const { field, op } = when;
  // The schema's range is {min, max}; an array is tolerated because early
  // drafts wrote one. Reading only the array shape printed "undefined–undefined"
  // on every real between-edge.
  const r = when.range;
  const v = r
    ? (Array.isArray(r) ? `${r[0]}–${r[1]}` : `${r.min}–${r.max}`)
    : formatValue(when.value);
  const OPS = { eq: '=', neq: '≠', lt: '<', lte: '≤', gt: '>', gte: '≥', in: 'in', not_in: 'not in', between: 'in' };
  return `${field} ${OPS[op] || op} ${v}`;
}

function formatValue(v) {
  if (Array.isArray(v)) return `[${v.join(', ')}]`;
  if (typeof v === 'string') return v;
  return String(v);
}

/** Parents of each node id. */
export function parentsOf(graph) {
  const parents = new Map(nodeIds(graph).map((id) => [id, []]));
  for (const e of edgeList(graph)) if (parents.has(e.to) && e.from !== e.to) parents.get(e.to).push(e.from);
  return parents;
}

/** Node ids reachable from the entry, in a stable order. */
export function reachable(graph) {
  const seen = new Set();
  const stack = graph?.entry ? [graph.entry] : [];
  while (stack.length) {
    const id = stack.pop();
    if (!id || seen.has(id) || !graph.nodes[id]) continue;
    seen.add(id);
    for (const e of graph.nodes[id].edges || []) stack.push(e.default !== undefined ? e.default : e.to);
  }
  return seen;
}

/**
 * Nodes on EVERY path from the entry to `nodeId` (its dominators, excluding
 * itself) — the only upstream a node may read (`inputs`) or a verdict may
 * take a score from. Straightforward fixed point over the parents map.
 */
export function dominatorsOf(graph, nodeId) {
  const ids = [...reachable(graph)];
  if (!ids.includes(nodeId)) return [];
  const parents = parentsOf(graph);
  const all = new Set(ids);
  const dom = new Map(ids.map((id) => [id, id === graph.entry ? new Set([id]) : new Set(all)]));
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of ids) {
      if (id === graph.entry) continue;
      const ps = (parents.get(id) || []).filter((p) => all.has(p));
      let next;
      if (!ps.length) next = new Set([id]);
      else {
        next = new Set(dom.get(ps[0]));
        for (const p of ps.slice(1)) for (const d of [...next]) if (!dom.get(p).has(d)) next.delete(d);
        next.add(id);
      }
      if (next.size !== dom.get(id).size || [...next].some((d) => !dom.get(id).has(d))) { dom.set(id, next); changed = true; }
    }
  }
  return [...dom.get(nodeId)].filter((d) => d !== nodeId);
}

/** Numeric output fields of a node's dominators: what a verdict's score can come from. */
export function numericUpstreamFields(graph, nodeId) {
  const out = [];
  for (const d of dominatorsOf(graph, nodeId)) {
    for (const [field, spec] of Object.entries(graph.nodes[d]?.output || {})) {
      if (spec.type === 'number') out.push({ node_id: d, field, min: spec.min, max: spec.max });
    }
  }
  return out;
}

/** The keys a step passes on beside its outcome: its declared context, or a judge's reasoning. */
export function contextKeysOf(node) {
  if (!node || node.type === 'verdict') return [];
  if (node.instrument === 'llm_judge') return [{ key: 'reasoning', type: 'text' }];
  if (node.instrument !== 'python') return [];
  return Object.entries(node.context || {}).map(([key, spec]) => ({ key, type: spec?.type || 'json' }));
}

/**
 * What a step can read from the steps before it: every dominator, with its
 * outcome fields and what it passes on, flat — the same namespace the
 * executor hands over as t.steps.<id>.
 */
export function stepsBefore(graph, nodeId) {
  return dominatorsOf(graph, nodeId)
    .filter((id) => graph.nodes[id] && graph.nodes[id].type !== 'verdict')
    .map((id) => {
      const node = graph.nodes[id];
      const keys = [
        ...Object.entries(node.output || {}).map(([key, spec]) => ({ key, type: spec?.type || 'json', outcome: true })),
        ...contextKeysOf(node),
      ];
      return { id, name: displayName(graph, id), keys };
    });
}

/** The rail group for those steps: one row per key, labelled by the step's name. */
export function stepsGroup(graph, nodeId) {
  const fields = stepsBefore(graph, nodeId).flatMap((step) => step.keys.map(({ key, type, outcome }) => ({
    path: `steps.${step.id}.${key}`, type, common: true,
    label: `${step.name} · ${key}${outcome ? ' (outcome)' : ''}`,
    python: `t.steps.${step.id}.${key}`, prompt: `{{steps.${step.id}.${key}}}`,
  })));
  return fields.length ? [{ id: 'steps', label: 'From earlier steps', fields }] : [];
}

/** A stable string for "has this graph changed" comparisons. */
export function stableKey(value) {
  const sort = (v) => {
    if (Array.isArray(v)) return v.map(sort);
    if (v && typeof v === 'object') return Object.fromEntries(Object.keys(v).sort().map((k) => [k, sort(v[k])]));
    return v;
  };
  return JSON.stringify(sort(value));
}

// ---------------------------------------------------------------------------
// Ids and names
// ---------------------------------------------------------------------------

const ID_RE = /^[A-Za-z][A-Za-z0-9_]*$/;

export function slugId(label, taken = new Set()) {
  let base = String(label || 'node').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 24) || 'node';
  if (!/^[a-z]/.test(base)) base = `n_${base}`;
  let id = base;
  let i = 2;
  while (taken.has(id)) { id = `${base}_${i}`; i += 1; }
  return id;
}

export function isValidId(id) {
  return ID_RE.test(id || '');
}

/** The name a node shows: its `name` if set, else its id. */
export function displayName(graph, id) {
  const n = graph?.nodes?.[id];
  return (n && n.name) || id;
}

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

export function emptyGraph() {
  return { schema_version: 2, entry: null, nodes: {} };
}

/** A fresh node of `kind`, named, with the kind's defaults. */
export function makeNode(kind, { name, target } = {}) {
  const meta = NODE_KINDS[kind];
  if (!meta) throw new Error(`unknown node kind "${kind}"`);
  const d = meta.defaults(target);
  const node = { type: meta.type, name: name || meta.label, ...d };
  // A kind's defaults may pick the instrument themselves (the Semantic check does).
  if (meta.instrument && !d.instrument) node.instrument = meta.instrument;
  return node;
}

export function updateNode(graph, id, patch) {
  const g = clone(graph);
  if (!g.nodes[id]) return g;
  g.nodes[id] = { ...g.nodes[id], ...patch };
  return g;
}

/**
 * Insert a new node on an edge: the edge now points at the new node, and the
 * new node's default edge points where the edge used to. This is the only way
 * a node is added, so a fresh node is always reachable and always terminates.
 *
 * `at` is { from, index } naming the edge, or { from } alone for a step that
 * leads nowhere yet (the step's default edge is made to point here);
 * `at: { entry: true }` inserts before the current entry.
 */
export function insertNode(graph, kind, at, { name, target } = {}) {
  const g = clone(graph);
  const id = slugId(name || NODE_KINDS[kind]?.label, new Set(nodeIds(g)));
  const node = makeNode(kind, { name, target });
  let oldTarget;
  if (at?.entry) {
    // The first step of a draft that had none is wired to NOTHING. Guessing
    // that its outcomes mean pass and fail is guessing what the eval is for,
    // and the person is about to say. The validator asks for the rest.
    oldTarget = g.entry || null;
    g.entry = id;
  } else if (at?.outcome != null) {
    // Added from one outcome of a step: that outcome now leads here, and it
    // keeps whatever it led to before, so inserting mid-flow does not break it.
    oldTarget = readOutcomes(g.nodes[at.from])?.outcomes[at.outcome]?.to || null;
  } else {
    const from = g.nodes[at.from];
    if (!from) throw new Error(`insertNode: no node "${at.from}"`);
    const edge = at.index != null && at.index >= 0 ? from.edges?.[at.index] : null;
    if (edge) {
      oldTarget = edge.default !== undefined ? edge.default : edge.to;
      if (edge.default !== undefined) edge.default = id; else edge.to = id;
    } else {
      // "+ after this step" on a step that leads nowhere yet: the person just
      // said where it leads. The step gets its default edge here; the new
      // step starts unconnected, like any other.
      oldTarget = null;
      from.edges = [...(from.edges || []).filter((e) => e.default === undefined), { default: id }];
    }
  }
  if (kind === 'verdict') {
    // a verdict has no edges; it simply replaces the target of that edge.
  } else {
    node.edges = defaultEdgesFor(node, oldTarget);
  }
  g.nodes[id] = node;
  if (at?.outcome != null) g.nodes[at.from].edges = routeOutcome(g.nodes[at.from], at.outcome, id);
  return { graph: g, id };
}

/**
 * Edges a fresh step starts with.
 *
 * Inserted ON a line, it keeps that line going — that is what "insert a step
 * here" means, and the flow through it is unchanged. Added with nothing after
 * it, it starts unconnected: where its outcomes go is the decision the person
 * came here to make, and a graph that quietly answered it for them is a graph
 * they have to notice and undo.
 */
function defaultEdgesFor(node, target) {
  return target ? [{ default: target }] : [];
}

/**
 * Add a brand-new verdict node and return its id (a pass or a fail). Used
 * when an edge is pointed at "a new verdict…".
 */
export function addVerdict(graph, { passed, reason_code, name } = {}) {
  const g = clone(graph);
  const id = slugId(name || (passed === false ? 'fail' : 'pass'), new Set(nodeIds(g)));
  const node = makeNode('verdict', { name: name || (passed === false ? 'Fail' : 'Pass') });
  node.config = { passed: passed !== false };
  if (passed === false && reason_code) node.config.reason_code = reason_code;
  g.nodes[id] = node;
  return { graph: g, id };
}

/**
 * Remove a node. Every edge that pointed at it is re-pointed at the node's
 * own default target (so the walk still terminates); if the node was the
 * entry, its default target becomes the entry. A verdict is removed only
 * when nothing points at it. Unreachable leftovers are pruned.
 */
export function removeNode(graph, id) {
  const g = clone(graph);
  const node = g.nodes[id];
  if (!node) return g;
  const parents = parentsOf(g).get(id) || [];
  if (node.type === 'verdict') {
    if (parents.length) throw new Error('This verdict is still the end of a path. Point those edges somewhere else first.');
    delete g.nodes[id];
    return g;
  }
  const dflt = (node.edges || []).find((e) => e.default !== undefined);
  const successor = dflt ? dflt.default : null;
  for (const p of parents) {
    for (const e of g.nodes[p].edges || []) {
      if (e.default === id) e.default = successor;
      if (e.to === id) e.to = successor;
    }
  }
  if (g.entry === id) g.entry = successor;
  delete g.nodes[id];
  return prune(g);
}

/** Drop nodes nothing reaches (a verdict left over after a rewire). */
export function prune(graph) {
  const g = clone(graph);
  const keep = reachable(g);
  for (const id of nodeIds(g)) if (!keep.has(id)) delete g.nodes[id];
  return g;
}

/** Replace a node's edges (the panel's edge editor writes the whole list). */
export function setEdges(graph, id, edges) {
  return updateNode(graph, id, { edges });
}

/** Rename a node's id everywhere it is referenced. */
export function renameNodeId(graph, oldId, newId) {
  if (oldId === newId) return graph;
  if (!isValidId(newId) || graph.nodes[newId]) throw new Error('That id is taken or not a valid identifier (letters, digits, _; starts with a letter).');
  const g = clone(graph);
  g.nodes[newId] = g.nodes[oldId];
  delete g.nodes[oldId];
  if (g.entry === oldId) g.entry = newId;
  for (const n of Object.values(g.nodes)) {
    for (const e of n.edges || []) { if (e.to === oldId) e.to = newId; if (e.default === oldId) e.default = newId; }
    if (Array.isArray(n.inputs)) n.inputs = n.inputs.map((x) => (x === oldId ? newId : x));
    if (n.config?.score_from?.node_id === oldId) n.config.score_from.node_id = newId;
    if (Array.isArray(n.override?.applies_to)) n.override.applies_to = n.override.applies_to.map((x) => (x === oldId ? newId : x));
  }
  return g;
}

// ---------------------------------------------------------------------------
// Layout — top-down, layered, computed every draw (never stored)
// ---------------------------------------------------------------------------

/**
 * @returns {Map<string, {x, y}>} position per node id
 */
export function layoutGraph(graph) {
  const ids = nodeIds(graph);
  const out = new Map();
  if (!ids.length) return out;
  const parents = parentsOf(graph);
  const rank = new Map();
  const visiting = new Set();
  const rankOf = (id) => {
    if (rank.has(id)) return rank.get(id);
    if (visiting.has(id)) return 0;
    visiting.add(id);
    const ps = parents.get(id) || [];
    const r = ps.length ? Math.max(...ps.map((p) => rankOf(p) + 1)) : 0;
    visiting.delete(id);
    rank.set(id, r);
    return r;
  };
  for (const id of ids) rankOf(id);
  if (graph.entry) rank.set(graph.entry, 0);
  // Verdicts sit on the bottom row so every path visibly ends at the same level.
  const maxRank = Math.max(...[...rank.values()]);
  const verdictRank = Math.max(maxRank, 1);
  for (const id of ids) if (kindOf(graph.nodes[id]) === 'verdict') rank.set(id, verdictRank);

  const rows = [];
  for (const id of ids) {
    const r = rank.get(id) || 0;
    (rows[r] = rows[r] || []).push(id);
  }
  // Where each step sits among its parent's outcomes. Children then read left
  // to right in the order the dots above them do, so the lines run straight
  // down instead of crossing for no reason.
  const byOutcome = new Map();
  for (const id of ids) {
    const shape = readOutcomes(graph.nodes[id]);
    if (!shape) continue;
    shape.outcomes.forEach((o, i) => { if (o.to && !byOutcome.has(o.to)) byOutcome.set(o.to, i); });
  }
  const outcomeAt = (id) => (byOutcome.has(id) ? byOutcome.get(id) : Number.MAX_SAFE_INTEGER);

  const placedX = new Map();
  rows.forEach((row, r) => {
    if (!row) return;
    if (r > 0) {
      const bary = (id) => {
        const ps = (parents.get(id) || []).filter((p) => placedX.has(p));
        return ps.length ? ps.reduce((s, p) => s + placedX.get(p), 0) / ps.length : Number.MAX_SAFE_INTEGER;
      };
      row.sort((a, b) => (bary(a) - bary(b)) || (outcomeAt(a) - outcomeAt(b)) || a.localeCompare(b));
    }
    const total = row.length * NODE_W + H_GAP * (row.length - 1);
    let x = -total / 2;
    for (const id of row) {
      placedX.set(id, x + NODE_W / 2);
      out.set(id, { x, y: r * (NODE_H + V_GAP) });
      x += NODE_W + H_GAP;
    }
  });
  return out;
}

// ---------------------------------------------------------------------------
// Local well-formedness (the server validates for real)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Canvas edits (TRO-449) — the same graph the panels edit, never a second model
// ---------------------------------------------------------------------------

/**
 * Positions a person dragged (definition.ui.layout) over the computed layout.
 * Only steps that exist are placed; anything malformed falls back to computed.
 */
export function mergeLayout(computed, saved) {
  if (!saved || typeof saved !== 'object') return computed;
  const out = new Map(computed);
  for (const [id, p] of Object.entries(saved)) {
    if (!out.has(id) || !p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    out.set(id, { x: p.x, y: p.y });
  }
  return out;
}

/** A starting condition on a step's first output field, so a drawn line is a valid edge. */
function conditionFor(field, spec) {
  if (!field || !spec) return null;
  if (spec.type === 'boolean') return { field, op: 'eq', value: true };
  if (spec.type === 'enum' && Array.isArray(spec.values) && spec.values.length) return { field, op: 'eq', value: spec.values[0] };
  if (spec.type === 'number') {
    const lo = Number.isFinite(spec.min) ? spec.min : 0;
    const hi = Number.isFinite(spec.max) ? spec.max : 1;
    return { field, op: 'gte', value: Number(((lo + hi) / 2).toFixed(2)) };
  }
  return null;
}

/**
 * A line drawn from one step to another. A step with no default way out gets
 * one; otherwise the line becomes a condition on the step's first output
 * field, placed ahead of the default, which the routing panel then edits.
 * @returns {{graph}|{error}}
 */
export function connectEdge(graph, from, to) {
  const src = graph?.nodes?.[from];
  const dst = graph?.nodes?.[to];
  if (!src || !dst) return { error: 'Both ends of a line must be steps of this eval.' };
  if (from === to) return { error: 'A step cannot lead to itself.' };
  if (src.type === 'verdict') return { error: 'A verdict ends the eval: it has no ways out.' };
  if (to === graph.entry) return { error: 'Nothing leads into the first step: it is where the eval starts.' };
  const edges = src.edges || [];
  if (edges.some((e) => (e.default !== undefined ? e.default : e.to) === to)) {
    return { error: `"${displayName(graph, from)}" already leads to "${displayName(graph, to)}".` };
  }
  const g = clone(graph);
  if (!edges.some((e) => e.default !== undefined)) {
    g.nodes[from].edges = [...clone(edges), { default: to }];
    return { graph: g };
  }
  const [field, spec] = Object.entries(src.output || {})[0] || [];
  const when = conditionFor(field, spec);
  if (!when) return { error: `Give "${displayName(graph, from)}" an output field first: a line needs a condition to branch on.` };
  const next = clone(edges);
  next.splice(next.findIndex((e) => e.default !== undefined), 0, { when, to });
  g.nodes[from].edges = next;
  return { graph: g };
}

/**
 * Delete a drawn line. The default way out cannot be deleted — every step
 * must still end somewhere — and steps nothing reaches any more are pruned.
 * @returns {{graph}|{error}}
 */
export function removeEdge(graph, from, index) {
  const e = graph?.nodes?.[from]?.edges?.[index];
  if (!e) return { error: 'There is no such line.' };
  if (e.default !== undefined) {
    return { error: `That is the default way out of "${displayName(graph, from)}". Point it somewhere else instead of removing it.` };
  }
  const g = clone(graph);
  g.nodes[from].edges.splice(index, 1);
  return { graph: prune(g) };
}

/** True for a draft with verdicts and no first step: valid to save, not to run (TRO-459). */
export function hasNoSteps(graph) {
  return Boolean(graph) && !graph.entry && Object.values(graph.nodes || {}).every((n) => n && n.type === 'verdict');
}

export function localProblems(graph) {
  const out = [];
  // A draft with no steps is not a problem: the server accepts it, and only enabling asks for a first step.
  if (!hasNoSteps(graph) && (!graph?.entry || !graph.nodes?.[graph.entry])) out.push({ node_id: '$graph', message: 'The eval needs a first node.' });
  for (const [id, n] of Object.entries(graph?.nodes || {})) {
    if (n.type === 'verdict') continue;
    const edges = n.edges || [];
    const shape = readOutcomes(n);
    const loose = shape ? shape.outcomes.filter((o) => !o.to) : [];
    if (loose.length) {
      out.push({ node_id: id, message: `Connect ${loose.length === shape.outcomes.length ? 'this step' : `“${loose[0].label}”`} to the step it should lead to.` });
    } else if (!edges.some((e) => e.default !== undefined)) {
      out.push({ node_id: id, message: 'Connect this step to the step it should lead to.' });
    }
    for (const e of edges) {
      const to = e.default !== undefined ? e.default : e.to;
      if (!graph.nodes[to]) out.push({ node_id: id, message: `An edge points at "${to}", which does not exist.` });
    }
    if (!n.output || !Object.keys(n.output).length) out.push({ node_id: id, message: 'Declare at least one output field.' });
    // A judge carries its own provider and model; there is no eval-level default to fall back on (TRO-460).
    if (n.instrument === 'llm_judge' && !(n.config && n.config.integration_id)) out.push({ node_id: id, message: 'Pick a provider and model for this judge.' });
    if (n.instrument === 'decision_model') {
      const rows = fromApi(n.config?.questions);
      const qp = Object.values(rowProblems(rows))[0];
      if (!(n.config && n.config.integration_id)) out.push({ node_id: id, message: 'Pick a decision model for this check.' });
      if (!rows.length) out.push({ node_id: id, message: 'Add at least one question.' });
      else if (qp) out.push({ node_id: id, message: qp[0] });
    }
    // A Semantic check with nothing to check against says so here, before the server does.
    if (n.instrument === 'cross_encoder' && Array.isArray(n.config?.pair) && n.config.pair[1] && typeof n.config.pair[1] === 'object' && !String(n.config.pair[1].text || '').trim()) {
      out.push({ node_id: id, message: 'Write the intent this step checks for.' });
    }
    if (n.instrument === 'cross_encoder' && ['intent', 'statement'].includes(n.config?.check) && !String(n.config.intent ?? n.config.statement ?? '').trim()) {
      out.push({ node_id: id, message: 'Write the intent this step checks for.' });
    }
    const spansCtx = Array.isArray(n.config?.pair) ? n.config.pair.find((x) => x && typeof x === 'object' && x.spans) : null;
    if (n.instrument === 'cross_encoder' && spansCtx && !['name', 'kind', 'tool_name'].some((k) => String(spansCtx.spans[k] || '').trim())) {
      out.push({ node_id: id, message: 'Pick which spans are the context.' });
    }
    if (n.instrument === 'bi_encoder' && n.config?.op === 'similarity' && Array.isArray(n.config.against?.texts) && !n.config.against.texts.length && !(n.config.against.ids || []).length) {
      out.push({ node_id: id, message: 'Add at least one example text to compare with.' });
    }
  }
  out.push(...deadEnds(graph));
  return out;
}

/**
 * Every path has to finish with a verdict.
 *
 * An eval that can walk somewhere and never come out the other side has no
 * answer for that trace, and the person who built it will not find out until
 * it happens in production. Reachability alone does not catch it: a step can
 * be perfectly reachable and still sit in a pocket of the graph — a pair of
 * steps pointing at each other, a branch that leads only back inward — with
 * no verdict downstream of it.
 *
 * So: walk BACKWARDS from the verdicts to find everything that can still
 * reach one, then flag anything the entry can reach that is not in that set.
 */
export function deadEnds(graph) {
  const nodes = graph?.nodes || {};
  if (!graph?.entry || !nodes[graph.entry]) return [];
  const parents = parentsOf(graph);
  const settles = new Set();
  const stack = Object.keys(nodes).filter((id) => nodes[id].type === 'verdict');
  stack.forEach((id) => settles.add(id));
  while (stack.length) {
    for (const up of parents.get(stack.pop()) || []) {
      if (!settles.has(up)) { settles.add(up); stack.push(up); }
    }
  }
  return [...reachable(graph)]
    .filter((id) => !settles.has(id))
    .sort()
    .map((id) => ({ node_id: id, message: 'This step never reaches a verdict. Every path has to end in a pass or a fail.' }));
}
